/**
 * 节点子步骤动态渲染组件（移动端版，行为对齐后台 NodeSubstepPanel）。
 *
 * 与后台完全同构：
 * - 子步骤配置（GET /container/substeps/config/{nodeCode}，过滤 ACTION_ENTRY）
 * - 状态记录（GET /container/substeps/records）+ check-complete/detail 必填口径
 * - 点「处理」→ 弹窗：附件规则（/container/nodeRules/{nodeCode}）+ 已传文件回显
 *   + 表单模板（form_template_id → GET /form/templates/{id}）+ 已填表单回填
 * - 完成：保存表单（POST /form/data，data=JSON 字符串）→ IOT_BIND 配置化绑定
 *   → PUT records/{id} 置 completed（data 一并带 JSON）→ checkNodeComplete → onComplete
 *
 * 差异（移动端简化）：不校验 iot:device:bind 权限（后端 bind 接口自身鉴权）；
 * docType 中文名通过 useDict('doc_type') 从后端字典解析（与 NodeDocUploader 对齐）。
 */
import {
  Box,
  Typography,
  LinearProgress,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Snackbar,
  CircularProgress,
  TextField,
  MenuItem,
} from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AddBoxIcon from '@mui/icons-material/AddBox';
import {
  getSubstepConfig,
  getSubstepRecords,
  initSubsteps,
  updateSubstepRecord,
  updateSubstepRecordWithData,
  checkSubstepComplete,
  checkNodeComplete,
  getNodeDocRules,
  uploadSubstepDocument,
  getSubstepDocuments,
  deleteSubstepDocument,
  type SubstepConfig,
  type NodeDocRuleItem,
} from '@/api/substep';
import { getNodeActionHint } from '@/api/fulfillment';
import { getFormTemplateById, getFormData, saveFormData, type FormDataItem } from '@/api/form';
import { uploadFileToServer, listFilesByBiz, getFileUrl, getFileInlineUrl, deleteFile, type FileAttachment } from '@/api/storage';
import { bindIotDevice } from '@/api/container';
import type { NodeActionHintVO } from '@/api/types';
import { useDict } from '@/hooks/useDict';
import { isWeChatBrowser } from '@/utils/device';
import ImagePreview from './ImagePreview';

/** 按文件名判断文件展示类型 */
function detectFileType(name: string): { isImage: boolean; isPdf: boolean } {
  const lower = name.toLowerCase();
  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(name);
  const isPdf = lower.endsWith('.pdf');
  return { isImage, isPdf };
}

/** 根据文件类型选择预览 URL 接口：PDF 走 inline-url 避免触发下载 */
function choosePreviewUrl(fileId: string, isPdf: boolean): Promise<string> {
  return isPdf ? getFileInlineUrl(fileId) : getFileUrl(fileId);
}

export interface NodeSubstepPanelProps {
  orderId: string;
  nodeCode: string;
  onComplete?: () => void;
  /** 建档动作入口（ARCHIVE_CREATE）：needArchiveCreate 时回调，由父级弹建柜 Dialog */
  onRequestNewArchive?: (prefill: NodeActionHintVO | null) => void;
  /** 建档动作入口状态变化回调（供父级感知 needArchiveCreate，以门控「提交推进」按钮） */
  onArchiveHintChange?: (hint: NodeActionHintVO | null) => void;
}

/** 状态颜色 / 标签 */
const STATUS_COLOR: Record<string, string> = {
  pending: '#9E9E9E',
  in_progress: '#1F6FE5',
  completed: '#18A06B',
};
const STATUS_LABEL: Record<string, string> = {
  pending: '待处理',
  in_progress: '处理中',
  completed: '已完成',
};

/**
 * IOT_BIND 配置化绑定参数（缺省对齐种子 load.thermometer）。
 * deviceTypeField 指向表单里承载「绑定类型」的字段键（缺省 bound_type，与 f...009 表单一致），
 * 其它表单可经 action_config.deviceTypeField 覆盖。
 */
interface IotBindConfig {
  deviceField: string;
  deviceTypeField: string;
  iotBindType: string;
  requiredPerm: string;
}
function parseIotBindConfig(actionConfig?: string): IotBindConfig {
  const fallback: IotBindConfig = {
    deviceField: 'device_no',
    deviceTypeField: 'bound_type',
    iotBindType: 'REEFER_MAIN',
    requiredPerm: 'iot:device:bind',
  };
  if (!actionConfig) return fallback;
  try {
    const parsed = JSON.parse(actionConfig) as Partial<IotBindConfig>;
    return {
      deviceField: parsed.deviceField || fallback.deviceField,
      deviceTypeField: parsed.deviceTypeField || fallback.deviceTypeField,
      iotBindType: parsed.iotBindType || fallback.iotBindType,
      requiredPerm: parsed.requiredPerm || fallback.requiredPerm,
    };
  } catch {
    return fallback;
  }
}

/** 多检查点单据文件（③）：node_documents 行在 panel 侧的呈现单元 */
interface CheckpointFile {
  /** node_documents 行 id（删除用） */
  id: string;
  name: string;
  fileId?: string;
  previewUrl?: string;
  isImage: boolean;
  isPdf?: boolean;
}

/** 解析规则的 checkpoints（③）：后端可能返回 jsonb 字符串或已解析数组；失败/空 → 空数组 */
function parseCheckpoints(rule: NodeDocRuleItem): { code: string; label: string; required?: boolean; min_count: number }[] {
  const raw = rule.checkpoints;
  if (!raw) return [];
  let arr: unknown = raw;
  if (typeof raw === 'string') {
    try {
      arr = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(arr)) return [];
  return arr
    .map((n: any) => ({
      code: n?.code,
      label: n?.label ?? n?.code,
      required: n?.required == null ? true : Boolean(n.required),
      min_count: Number(n?.min_count) || 0,
    }))
    .filter((c: { code?: string; min_count: number }) => c.code && c.min_count > 0);
}

/** 已上传文件（按 docType 分组） */
interface UploadedFile {
  name: string;
  fileId?: string;
  previewUrl?: string;
  isImage: boolean;
  isPdf?: boolean;
}

export default function NodeSubstepPanel({
  orderId,
  nodeCode,
  onComplete,
  onRequestNewArchive,
  onArchiveHintChange,
}: NodeSubstepPanelProps) {
  // docType 中文名走字典（doc_type），未命中回退原始 code
  const { getLabel } = useDict();
  const [archiveHint, setArchiveHint] = useState<NodeActionHintVO | null>(null);
  const [substeps, setSubsteps] = useState<SubstepConfig[]>([]);
  const [records, setRecords] = useState<Array<Record<string, unknown> & { substepCode: string; status: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

  // 处理弹窗状态
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [processingSubstep, setProcessingSubstep] = useState<SubstepConfig | null>(null);
  const [substepRules, setSubstepRules] = useState<NodeDocRuleItem[]>([]);
  // 每种附件类型保留已上传文件列表：单份规则数组长度最多为 1，多份规则（allowMultiple）可持续追加
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, UploadedFile[]>>({});
  // 多检查点单据（③）：docType → checkpointCode → 已传文件列表（仅 checkpoint 规则维护）
  const [checkpointDocs, setCheckpointDocs] = useState<Record<string, Record<string, CheckpointFile[]>>>({});
  const [loadingRules, setLoadingRules] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formTemplate, setFormTemplate] = useState<any>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [substepDetail, setSubstepDetail] = useState<any>(null);
  const [previewFile, setPreviewFile] = useState<{ url: string; name: string; type?: 'image' | 'pdf' } | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [config, recordsData, detail] = await Promise.all([
        getSubstepConfig(nodeCode, orderId).catch(() => []),
        getSubstepRecords(orderId, nodeCode).catch(() => []),
        checkSubstepComplete(orderId, nodeCode).catch(() => null),
      ]);
      setSubsteps(
        (Array.isArray(config) ? config : []).filter((s) => s.executionMode !== 'ACTION_ENTRY'),
      );
      setRecords(Array.isArray(recordsData) ? (recordsData as any[]) : []);
      setSubstepDetail(detail && typeof detail === 'object' ? detail : null);
      // 建档动作入口（仅 load 节点有意义；失败静默）
      if (nodeCode === 'load') {
        try {
          const hint = await getNodeActionHint(orderId, nodeCode);
          const resolved = hint && hint.needArchiveCreate ? hint : null;
          setArchiveHint(resolved);
          onArchiveHintChange?.(resolved);
        } catch {
          setArchiveHint(null);
          onArchiveHintChange?.(null);
        }
      } else {
        setArchiveHint(null);
        onArchiveHintChange?.(null);
      }
    } catch (err) {
      console.error('加载子步骤数据失败', err);
    } finally {
      setLoading(false);
    }
  }, [orderId, nodeCode]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getRecord = (substepCode: string) =>
    records.find((r) => r.substepCode === substepCode);

  const detailLoaded = substepDetail != null;
  const requiredTotal = detailLoaded
    ? substepDetail.requiredTotal
    : substeps.filter((s) => s.isRequired).length;
  const completedRequired = detailLoaded
    ? substepDetail.requiredCompleted
    : substeps.filter((s) => s.isRequired).filter((s) => getRecord(s.substepCode)?.status === 'completed').length;
  const progress = requiredTotal > 0 ? (completedRequired / requiredTotal) * 100 : 100;
  const isAllCompleted = detailLoaded ? substepDetail.completed : false;

  const canStart = (substep: SubstepConfig): boolean => {
    if (substep.executionMode === 'parallel') return true;
    if (!substep.dependsOn) return true;
    const depRecord = getRecord(substep.dependsOn as string);
    return depRecord?.status === 'completed';
  };

  const getStatus = (substep: SubstepConfig): string => {
    const record = getRecord(substep.substepCode);
    if (record?.status === 'completed') return 'completed';
    if (record?.status === 'in_progress') return 'in_progress';
    if (canStart(substep)) return 'pending';
    return 'locked';
  };

  /** 打开处理弹窗：并行加载附件规则 + 已传文件 + 表单模板 + 已填数据 */
  const handleOpenProcessDialog = async (substep: SubstepConfig) => {
    setProcessingSubstep(substep);
    setUploadedFiles({});
    setFormValues({});
    setFormTemplate(null);
    setProcessDialogOpen(true);
    setLoadingRules(true);
    try {
      const [rules, existingFiles, template] = await Promise.all([
        getNodeDocRules(substep.nodeCode ?? nodeCode, orderId).catch(() => []),
        listFilesByBiz('node_doc', orderId, substep.nodeCode ?? nodeCode).catch(() => []),
        substep.formTemplateId ? getFormTemplateById(substep.formTemplateId).catch(() => null) : Promise.resolve(null),
      ]);
      const filteredRules = (Array.isArray(rules) ? rules : []).filter((r) => {
        if (!r.substepCode) return false;
        return r.substepCode === substep.substepCode;
      });
      setSubstepRules(filteredRules);

      const filesArray = Array.isArray(existingFiles) ? (existingFiles as FileAttachment[]) : [];
      // 允许多份规则保留全部历史已传；单份规则只回显最新一份（重新上传语义）
      const filesByDocType: Record<string, UploadedFile[]> = {};
      for (const rule of filteredRules) {
        const matched = filesArray
          .filter((f) => f.bizId3 === rule.docType)
          .sort((a, b) =>
            String((b as { createdAt?: string }).createdAt ?? '').localeCompare(
              String((a as { createdAt?: string }).createdAt ?? ''),
            ),
          );
        const selected = rule.allowMultiple ? matched : matched.slice(0, 1);
        if (selected.length === 0) continue;
        const built: UploadedFile[] = [];
        for (const mf of selected) {
          const name = mf.fileName ?? '文件';
          const { isImage, isPdf } = detectFileType(name);
          let previewUrl: string | undefined;
          try {
            previewUrl = await choosePreviewUrl(mf.id, isPdf);
          } catch {
            previewUrl = undefined;
          }
          built.push({ name, fileId: mf.id, previewUrl, isImage, isPdf });
        }
        filesByDocType[rule.docType] = built;
      }
      setUploadedFiles(filesByDocType);

      // ③ 多检查点单据：按 docType 从 node_documents 取已传文件，按 checkpointCode 分组
      const cpRules = filteredRules.filter((r) => parseCheckpoints(r).length > 0);
      const cpDocs: Record<string, Record<string, CheckpointFile[]>> = {};
      for (const r of cpRules) {
        const docs = await getSubstepDocuments(orderId, substep.substepCode, r.docType).catch(() => [] as any[]);
        const byCp: Record<string, CheckpointFile[]> = {};
        for (const d of docs) {
          const cp = d.checkpointCode || '__none__';
          const name = d.fileName || '文件';
          const { isImage, isPdf } = detectFileType(name);
          let previewUrl: string | undefined;
          if (d.fileId) {
            try {
              previewUrl = await choosePreviewUrl(d.fileId, isPdf);
            } catch {
              previewUrl = undefined;
            }
          }
          const f: CheckpointFile = { id: d.id, name, fileId: d.fileId, previewUrl, isImage, isPdf };
          if (!byCp[cp]) byCp[cp] = [];
          byCp[cp].push(f);
        }
        cpDocs[r.docType] = byCp;
      }
      setCheckpointDocs(cpDocs);

      if (template) {
        setFormTemplate(template);
        try {
          const formDataList = await getFormData(orderId, substep.nodeCode ?? nodeCode).catch(() => []);
          const existing = (Array.isArray(formDataList) ? formDataList : []).find(
            (d: FormDataItem) => d.templateId === template.id && d.substepCode === substep.substepCode,
          );
          if (existing) {
            try {
              setFormValues(JSON.parse(existing.data));
            } catch {
              setFormValues({});
            }
          }
        } catch (err) {
          console.error('加载表单数据失败', err);
        }
      }
    } catch (err) {
      console.error('加载附件规则失败', err);
      setSubstepRules([]);
    } finally {
      setLoadingRules(false);
    }
  };

  /** 上传附件（对齐后台：bizType=node_doc，bizId3=docType）；允许多份时追加，单份时覆盖 */
  const handleFileUpload = async (docType: string, file: File) => {
    const allowMultiple = substepRules.find((r) => r.docType === docType)?.allowMultiple === true;
    try {
      const result = await uploadFileToServer(file, 'node_doc', {
        bizId: orderId,
        bizId2: processingSubstep?.nodeCode ?? nodeCode,
        bizId3: docType,
      });
      if (result && result.id) {
        const { isImage, isPdf } = detectFileType(file.name);
        let previewUrl: string | undefined;
        try {
          previewUrl = await choosePreviewUrl(result.id, isPdf);
        } catch {
          previewUrl = undefined;
        }
        const entry: UploadedFile = { name: file.name, fileId: result.id, previewUrl, isImage, isPdf };
        setUploadedFiles((prev) => ({
          ...prev,
          [docType]: allowMultiple ? [...(prev[docType] || []), entry] : [entry],
        }));
      }
    } catch (err) {
      console.error('文件上传失败', err);
      // 降级到本地预览（仅图片可本地预览；PDF 不转 base64）
      const { isImage, isPdf } = detectFileType(file.name);
      if (!isImage) {
        const entry: UploadedFile = { name: file.name, fileId: undefined, previewUrl: undefined, isImage: false, isPdf };
        setUploadedFiles((prev) => ({
          ...prev,
          [docType]: allowMultiple ? [...(prev[docType] || []), entry] : [entry],
        }));
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        const entry: UploadedFile = { name: file.name, previewUrl: ev.target?.result as string, isImage: true, isPdf: false };
        setUploadedFiles((prev) => ({
          ...prev,
          [docType]: allowMultiple ? [...(prev[docType] || []), entry] : [entry],
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteFile = async (docType: string, fileIndex: number) => {
    const file = uploadedFiles[docType]?.[fileIndex];
    if (file?.fileId) {
      try {
        await deleteFile(file.fileId);
      } catch (err) {
        console.error('删除文件失败', err);
      }
    }
    setUploadedFiles((prev) => {
      const arr = (prev[docType] || []).filter((_, index) => index !== fileIndex);
      const next = { ...prev };
      if (arr.length > 0) next[docType] = arr;
      else delete next[docType];
      return next;
    });
  };

  /** ③ 多检查点：先落 file_attachments 取 fileId，再经 uploadSubstepDocument 落 node_documents（带 checkpointCode） */
  const handleCheckpointUpload = async (docType: string, checkpointCode: string, file: File) => {
    if (!processingSubstep) return;
    try {
      const up = await uploadFileToServer(file, 'node_doc', {
        bizId: orderId,
        bizId2: processingSubstep.nodeCode ?? nodeCode,
        bizId3: docType,
      });
      const fileId = up?.id;
      if (!fileId) throw new Error('文件上传失败');
      const doc = await uploadSubstepDocument({
        orderId,
        nodeCode: processingSubstep.nodeCode ?? nodeCode,
        substepCode: processingSubstep.substepCode,
        docType,
        fileId,
        checkpointCode,
      });
      const { isImage, isPdf } = detectFileType(file.name);
      let previewUrl: string | undefined;
      try {
        previewUrl = await choosePreviewUrl(fileId, isPdf);
      } catch {
        previewUrl = undefined;
      }
      const docId = (doc as any)?.id || fileId;
      setCheckpointDocs((prev) => {
        const byCp = { ...(prev[docType] || {}) };
        const arr = byCp[checkpointCode] ? [...byCp[checkpointCode]] : [];
        arr.push({ id: docId, name: file.name, fileId, previewUrl, isImage, isPdf });
        byCp[checkpointCode] = arr;
        return { ...prev, [docType]: byCp };
      });
    } catch (err) {
      console.error('检查点文件上传失败', err);
      setSnackbar({ open: true, message: '上传失败' });
    }
  };

  /** ③ 多检查点：删除单张检查点照片（仅删 node_documents 行） */
  const handleCheckpointDelete = async (docType: string, checkpointCode: string, docId: string) => {
    try {
      await deleteSubstepDocument(docId);
    } catch (err) {
      console.error('删除检查点文件失败', err);
    }
    setCheckpointDocs((prev) => {
      const byCp = { ...(prev[docType] || {}) };
      if (byCp[checkpointCode]) {
        byCp[checkpointCode] = byCp[checkpointCode].filter((f) => f.id !== docId);
      }
      return { ...prev, [docType]: byCp };
    });
  };

  const handleFormChange = (name: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const allRequiredUploaded = substepRules
    .filter((r) => r.required)
    .every((r) => {
      const cps = parseCheckpoints(r);
      if (cps.length > 0) {
        const byCp = checkpointDocs[r.docType] || {};
        return cps
          .filter((cp) => cp.required !== false)
          .every((cp) => (byCp[cp.code] || []).length >= cp.min_count);
      }
      return (uploadedFiles[r.docType]?.length ?? 0) > 0;
    });

  /** 完成子步骤核心（保存表单 / IoT 绑定 / 置 completed），与后台 completeSubstepCore 同构
   *  注：原先对 seal_iot 的「封条照片」专项前置校验已移除——substepCode='seal_iot' 已随
   *  CIF 17 节点重写退休（2026-09-20），且与通用必填闸门 allRequiredUploaded 完全重复。 */
  const completeSubstepCore = async () => {
    if (!processingSubstep) return;
    setSubmitting(true);
    try {
      if (formTemplate && Object.keys(formValues).length > 0) {
        await saveFormData({
          orderId,
          nodeCode: processingSubstep.nodeCode ?? nodeCode,
          substepCode: processingSubstep.substepCode,
          templateId: formTemplate.id,
          data: JSON.stringify(formValues),
          status: 'submitted',
        });
      }

      // IOT_BIND 子步骤（如 load.thermometer 温度计）：配置化绑定
      // （deviceField/deviceTypeField/iotBindType 均来自 actionConfig，禁止硬编码字段键）
      if (processingSubstep.actionType === 'IOT_BIND') {
        const cfg = parseIotBindConfig(String(processingSubstep.actionConfig ?? ''));
        const deviceNo = (formValues[cfg.deviceField] || '').trim();
        if (deviceNo) {
          try {
            const containers = await (await import('@/api/container')).getContainersByOrder(orderId);
            const list = Array.isArray(containers) ? containers : [];
            // 优先匹配当前订单的柜档案；一单多柜场景下取第一条
            const containerId = list.find((c: any) => c.orderId === orderId)?.id || list[0]?.id;
            if (!containerId) {
              throw new Error('未找到关联柜档案，无法绑定设备');
            }
            await bindIotDevice(containerId, {
              deviceNo,
              boundType: (formValues[cfg.deviceTypeField] || cfg.iotBindType).trim(),
              remark: formValues['remark'] || '',
              operator: 'mobile',
            });
          } catch (bindErr: unknown) {
            console.error('IoT 设备绑定失败', bindErr);
            setSnackbar({
              open: true,
              message: (bindErr as { message?: string })?.message || 'IoT 设备绑定失败',
            });
            setSubmitting(false);
            return;
          }
        }
      }

      // 两层必填校验口径：record.data 承载表单 JSON（与后端 SubstepRequirementService 判定一致）
      const substepData =
        formTemplate && Object.keys(formValues).length > 0
          ? JSON.stringify(formValues)
          : undefined;

      const record = getRecord(processingSubstep.substepCode);
      if (record && record.id) {
        await updateSubstepRecordWithData(String(record.id), 'completed', substepData);
      } else {
        await initSubsteps({ orderId, nodeCode: processingSubstep.nodeCode ?? nodeCode });
        const newRecords = await getSubstepRecords(orderId, processingSubstep.nodeCode ?? nodeCode).catch(() => []);
        const newRecord = (Array.isArray(newRecords) ? newRecords : []).find(
          (r) => r.substepCode === processingSubstep.substepCode,
        );
        if (newRecord) {
          await updateSubstepRecordWithData(String(newRecord.id), 'completed', substepData);
        }
      }

      setSnackbar({ open: true, message: '子步骤已完成' });
      setProcessDialogOpen(false);
      setProcessingSubstep(null);
      await loadData();
      try {
        const checkResult = await checkNodeComplete(orderId, nodeCode);
        if (checkResult) onComplete?.();
      } catch {
        /* 检查失败不阻断 */
      }
    } catch (err) {
      console.error('完成子步骤失败', err);
      setSnackbar({ open: true, message: (err as { message?: string })?.message || '操作失败' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <CircularProgress size={20} />
        <Typography sx={{ fontSize: '12px', color: '#9E9E9E', mt: 1 }}>加载中...</Typography>
      </Box>
    );
  }

  // 无子步骤且无建柜入口：直接不渲染，避免占据空白卡片并提示“无需处理”
  if (substeps.length === 0) {
    if (archiveHint?.needArchiveCreate && onRequestNewArchive) {
      return (
        <Box
          sx={{
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '18px',
            bgcolor: 'background.paper',
            boxShadow: '0 1px 2px rgba(16,24,40,.04),0 6px 18px rgba(16,24,40,.07)',
            mb: 2,
          }}
        >
          <Typography sx={{ fontSize: '15px', fontWeight: 700, mb: 0.5 }}>子步骤进度</Typography>
          <Box
            sx={{
              mt: 1,
              p: 1.5,
              borderRadius: '10px',
              bgcolor: '#E8F0FE',
              border: '1px solid #90CAF9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography sx={{ fontSize: '12px', color: '#0D47A1' }}>
              本节点支持建立柜档案
              {archiveHint.prefillContainerNo ? `（预填柜号：${archiveHint.prefillContainerNo}）` : ''}
              {archiveHint.bookingNo ? `（预填订舱号：${archiveHint.bookingNo}）` : ''}
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddBoxIcon />}
              onClick={() => onRequestNewArchive(archiveHint)}
              sx={{ fontSize: '12px', flexShrink: 0 }}
            >
              建立柜档案
            </Button>
          </Box>
        </Box>
      );
    }
    return null;
  }

  const executionMode = substeps[0]?.executionMode;
  const hasForm = Boolean(formTemplate?.formSchema);

  return (
    <Box
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '18px',
        bgcolor: 'background.paper',
        boxShadow: '0 1px 2px rgba(16,24,40,.04),0 6px 18px rgba(16,24,40,.07)',
        mb: 2,
      }}
    >
      {/* 标题 + 进度 */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '15px', fontWeight: 700 }}>子步骤进度</Typography>
          <Chip
            label={executionMode === 'parallel' ? '并行执行' : '串行执行'}
            size="small"
            sx={{ fontSize: '10px', height: 20 }}
          />
        </Box>
        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
          {detailLoaded
            ? requiredTotal > 0
              ? `${completedRequired}/${requiredTotal} 必填已完成`
              : '无必填子步骤，可推进'
            : ''}
        </Typography>
      </Box>

      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          mb: 1.5,
          height: 6,
          borderRadius: 3,
          backgroundColor: 'divider',
          '& .MuiLinearProgress-bar': {
            borderRadius: 3,
            backgroundColor: isAllCompleted ? '#18A06B' : '#1F6FE5',
          },
        }}
      />

      {/* 建档动作入口（ARCHIVE_CREATE，由 node-action-hint 驱动） */}
      {archiveHint?.needArchiveCreate && onRequestNewArchive && (
        <Box
          sx={{
            mb: 1.5,
            p: 1.5,
            borderRadius: '10px',
            bgcolor: '#E8F0FE',
            border: '1px solid #90CAF9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: '12px', color: '#0D47A1' }}>
            本节点支持建立柜档案
            {archiveHint.prefillContainerNo ? `（预填柜号：${archiveHint.prefillContainerNo}）` : ''}
            {archiveHint.bookingNo ? `（预填订舱号：${archiveHint.bookingNo}）` : ''}
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddBoxIcon />}
            onClick={() => onRequestNewArchive(archiveHint)}
            sx={{ fontSize: '12px', flexShrink: 0 }}
          >
            建立柜档案
          </Button>
        </Box>
      )}

      {/* 子步骤列表 */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {substeps.map((substep, idx) => {
          const status = getStatus(substep);
          return (
            <Box
              key={substep.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                border: '1px solid',
                borderColor: status === 'completed' ? '#18A06B' : substep.isRequired ? '#FFCCBC' : 'divider',
                borderRadius: '8px',
                bgcolor: status === 'completed' ? '#E8F5E9' : 'background.paper',
              }}
            >
              {/* 步骤序号 */}
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: STATUS_COLOR[status] || '#E0E0E0',
                  color: '#FFF',
                  flexShrink: 0,
                }}
              >
                {status === 'completed' ? (
                  <CheckCircleIcon sx={{ fontSize: 18 }} />
                ) : (
                  <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>{substep.substepSeq ?? idx + 1}</Typography>
                )}
              </Box>
              {/* 信息 */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: 500,
                      color: status === 'locked' ? 'text.disabled' : 'text.primary',
                    }}
                  >
                    {substep.substepName}
                    {substep.isRequired && (
                      <Typography component="span" sx={{ color: '#E53935', ml: 0.25 }}>
                        *
                      </Typography>
                    )}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: STATUS_COLOR[status] || 'text.disabled' }}>
                    {STATUS_LABEL[status] || status}
                  </Typography>
                </Box>
                {substep.description && (
                  <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: 0.25 }}>
                    {substep.description}
                  </Typography>
                )}
              </Box>
              {/* 操作 */}
              <Box sx={{ flexShrink: 0 }}>
                {status === 'completed' ? (
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleOpenProcessDialog(substep)}
                    sx={{ fontSize: '11px', color: '#18A06B' }}
                  >
                    查看/更新
                  </Button>
                ) : status !== 'locked' ? (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<PlayArrowIcon />}
                    onClick={() => handleOpenProcessDialog(substep)}
                    sx={{ fontSize: '11px' }}
                  >
                    处理
                  </Button>
                ) : null}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* 处理弹窗 */}
      <Dialog open={processDialogOpen} onClose={() => { setProcessDialogOpen(false); setProcessingSubstep(null); }} maxWidth="sm" fullWidth>
        <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '15px' }}>{processingSubstep?.substepName}</Typography>
            <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>{processingSubstep?.description}</Typography>
          </Box>
          <IconButton onClick={() => { setProcessDialogOpen(false); setProcessingSubstep(null); }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {loadingRules ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              {substepRules.length === 0 && !hasForm && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography sx={{ color: 'text.disabled', fontSize: '13px' }}>该子步骤无需上传附件</Typography>
                </Box>
              )}

              {/* 附件上传区域 */}
              {substepRules.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AttachFileIcon sx={{ fontSize: 16 }} /> 附件上传
                  </Typography>
                  {isWeChatBrowser() && (
                    <Typography sx={{ fontSize: '11px', color: '#E65100', mb: 1 }}>
                      微信内仅支持拍摄/相册图片上传；如需上传 PDF 文件，请点击右上角 ··· 选择「在浏览器打开」
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {substepRules.map((rule) => {
                      const cps = parseCheckpoints(rule);
                      if (cps.length > 0) {
                        return (
                          <CheckpointRuleBlock
                            key={rule.docType}
                            rule={rule}
                            checkpoints={cps}
                            docs={checkpointDocs[rule.docType] || {}}
                            docLabel={getLabel('doc_type', rule.docType)}
                            onUpload={handleCheckpointUpload}
                            onDelete={handleCheckpointDelete}
                            onPreview={(file) => setPreviewFile(file)}
                          />
                        );
                      }
                      return (
                        <RuleItem
                          key={rule.docType}
                          rule={rule}
                          files={uploadedFiles[rule.docType] || []}
                          allowMultiple={rule.allowMultiple === true}
                          docLabel={getLabel('doc_type', rule.docType)}
                          onUpload={(file) => handleFileUpload(rule.docType, file)}
                          onDelete={(idx) => handleDeleteFile(rule.docType, idx)}
                          onPreview={(file) => setPreviewFile(file)}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* 表单填写区域 */}
              {hasForm && (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    📋 表单填写
                  </Typography>
                  <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: '8px', bgcolor: 'grey.50' }}>
                    {parseFields(formTemplate.formSchema).map((field: any) => (
                      <Box key={field.name} sx={{ mb: 1.5 }}>
                        <Typography sx={{ fontSize: '12px', mb: 0.5 }}>
                          {field.label} {field.required && <span style={{ color: '#E53935' }}>*</span>}
                        </Typography>
                        {field.type === 'textarea' ? (
                          <TextField
                            fullWidth
                            size="small"
                            multiline
                            rows={2}
                            placeholder={field.placeholder}
                            value={formValues?.[field.name] || ''}
                            onChange={(e) => handleFormChange(field.name, e.target.value)}
                          />
                        ) : field.type === 'select' ? (
                          <TextField
                            fullWidth
                            size="small"
                            select
                            value={formValues?.[field.name] || ''}
                            onChange={(e) => handleFormChange(field.name, e.target.value)}
                          >
                            {(field.options || []).map((opt: any) => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </TextField>
                        ) : (
                          <TextField
                            fullWidth
                            size="small"
                            type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                            placeholder={field.placeholder}
                            value={formValues?.[field.name] || ''}
                            onChange={(e) => handleFormChange(field.name, e.target.value)}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* 完成条件提示 */}
              <Box sx={{
                p: 1.5,
                bgcolor: allRequiredUploaded && isFormComplete(formTemplate, formValues) ? '#E8F5E9' : '#FFF3E0',
                borderRadius: '8px',
                mb: 1,
              }}>
                <Typography sx={{ fontSize: '12px', color: allRequiredUploaded && isFormComplete(formTemplate, formValues) ? '#2E7D32' : '#E65100' }}>
                  {allRequiredUploaded && isFormComplete(formTemplate, formValues)
                    ? '✓ 所有必填项已完成'
                    : '请完成必填附件与表单后再提交'}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <Box sx={{ px: 2, pb: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button size="small" onClick={() => { setProcessDialogOpen(false); setProcessingSubstep(null); }}>
            取消
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={completeSubstepCore}
            disabled={(!allRequiredUploaded || !isFormComplete(formTemplate, formValues)) || submitting}
          >
            {submitting ? <CircularProgress size={14} sx={{ mr: 0.5 }} /> : null}
            完成
          </Button>
        </Box>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <ImagePreview
        open={!!previewFile}
        url={previewFile?.url}
        name={previewFile?.name}
        type={previewFile?.type}
        onClose={() => setPreviewFile(null)}
      />
    </Box>
  );
}

/** 解析 form_schema（与后台 parseFields 同构） */
function parseFields(schemaStr: string): any[] {
  try {
    const schema = JSON.parse(schemaStr);
    return schema.fields || [];
  } catch {
    return [];
  }
}

/** 表单是否填写完整（所有必填字段非空） */
function isFormComplete(template: any, formValues: Record<string, string>): boolean {
  if (!template?.formSchema) return true;
  return parseFields(template.formSchema)
    .filter((f: any) => f.required)
    .every((f: any) => formValues?.[f.name]);
}

/** 附件规则项（移动端版，支持允许多份：可多选、可继续添加、逐份删除；对齐后台 RuleItem） */
function RuleItem({
  rule,
  files,
  allowMultiple,
  docLabel,
  onUpload,
  onDelete,
  onPreview,
}: {
  rule: NodeDocRuleItem;
  files: UploadedFile[];
  allowMultiple: boolean;
  /** 单证中文名（由父组件 useDict 解析后传入，避免在子组件内重复挂 hook） */
  docLabel: string;
  onUpload: (file: File) => void;
  onDelete: (index: number) => void;
  /** 点击图片/PDF 时回调（用于弹出预览） */
  onPreview?: (file: { url: string; name: string; type?: 'image' | 'pdf' }) => void;
}) {
  const uploaded = files.length > 0;
  return (
    <Box sx={{
      p: 1.5,
      border: '1px solid',
      borderColor: uploaded ? '#18A06B' : 'divider',
      borderRadius: '8px',
      bgcolor: uploaded ? '#F1F8E9' : 'background.paper',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: '13px' }}>
            {docLabel}
          </Typography>
          {rule.required && <Typography sx={{ fontSize: '11px', color: '#E53935' }}>*</Typography>}
          {uploaded && (
            <Typography sx={{ fontSize: '11px', color: '#2E7D32', ml: 1 }}>
              ✓ 已上传{allowMultiple ? ` ${files.length} 份` : ''}
            </Typography>
          )}
        </Box>
        <Button
          size="small"
          component="label"
          variant={uploaded ? 'text' : 'outlined'}
          sx={{ fontSize: '11px', color: uploaded ? '#F0A020' : undefined }}
        >
          {allowMultiple ? (uploaded ? '继续添加' : '上传多份') : uploaded ? '重新上传' : '上传'}
          <input
            type="file"
            accept={isWeChatBrowser() ? 'image/*' : '.pdf,.jpg,.jpeg,.png'}
            multiple={allowMultiple}
            hidden
            onChange={(e) => {
              const picked = Array.from(e.target.files ?? []);
              picked.forEach((file) => onUpload(file));
              e.target.value = '';
            }}
          />
        </Button>
      </Box>
      {files.map((f, idx) => (
        <Box
          key={f.fileId || `${f.name}-${idx}`}
          sx={{
            mt: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 0.75,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '6px',
          }}
        >
          {(() => {
            if (f.isImage && f.previewUrl) {
              return (
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '4px',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                    cursor: f.previewUrl && onPreview ? 'pointer' : 'default',
                  }}
                  onClick={() => {
                    if (f.previewUrl && onPreview) onPreview({ url: f.previewUrl, name: f.name, type: f.isPdf ? 'pdf' : 'image' });
                  }}
                >
                  <img src={f.previewUrl} alt={f.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
              );
            }
            if (f.isPdf) {
              return (
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '4px',
                    bgcolor: '#FFEBEE',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    color: '#C62828',
                    flexShrink: 0,
                    cursor: f.previewUrl && onPreview ? 'pointer' : 'default',
                  }}
                  onClick={() => {
                    if (f.previewUrl && onPreview) onPreview({ url: f.previewUrl, name: f.name, type: 'pdf' });
                  }}
                >
                  PDF
                </Box>
              );
            }
            return (
              <Box sx={{ width: 40, height: 40, borderRadius: '4px', bgcolor: '#FFF3E0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 }}>
                📄
              </Box>
            );
          })()}
          <Typography sx={{ fontSize: '12px', color: 'primary.main', flex: 1, wordBreak: 'break-all' }}>{f.name}</Typography>
          <IconButton size="small" title={allowMultiple ? '删除这份文件' : '删除后可重新上传'} onClick={() => onDelete(idx)} sx={{ color: '#E53935' }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
}

/**
 * 多检查点单据块（③，移动端版）。一条 docType 规则拆成 N 个检查点子卡片，
 * 每个检查点独立上传（落 node_documents，带 checkpointCode）/删除，实时显示 已传/要求 张数。
 */
function CheckpointRuleBlock({
  rule,
  checkpoints,
  docs,
  docLabel,
  onUpload,
  onDelete,
  onPreview,
}: {
  rule: NodeDocRuleItem;
  checkpoints: { code: string; label: string; required?: boolean; min_count: number }[];
  docs: Record<string, CheckpointFile[]>;
  docLabel: string;
  onUpload: (docType: string, checkpointCode: string, file: File) => void;
  onDelete: (docType: string, checkpointCode: string, docId: string) => void;
  /** 点击图片/PDF 时回调（用于弹出预览） */
  onPreview?: (file: { url: string; name: string; type?: 'image' | 'pdf' }) => void;
}) {
  return (
    <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: '8px', bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{docLabel}</Typography>
        {rule.required && <Typography sx={{ fontSize: '11px', color: '#E53935' }}>*</Typography>}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {checkpoints.map((cp) => {
          const files = docs[cp.code] || [];
          const checkpointRequired = cp.required !== false;
          const met = files.length >= cp.min_count;
          return (
            <Box
              key={cp.code}
              sx={{
                p: 1,
                border: '1px solid',
                borderColor: met ? '#18A06B' : 'divider',
                borderRadius: '6px',
                bgcolor: met ? '#F1F8E9' : 'grey.50',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: '12px' }}>{cp.label}</Typography>
                  <Typography sx={{ fontSize: '11px', color: checkpointRequired ? '#E53935' : 'text.secondary' }}>
                    {checkpointRequired ? '必填' : '选填'}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: met ? '#2E7D32' : checkpointRequired ? '#E65100' : 'text.secondary' }}>
                    {files.length}/{cp.min_count}
                  </Typography>
                </Box>
                <Button size="small" component="label" variant="outlined" sx={{ fontSize: '11px' }}>
                  上传
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) onUpload(rule.docType, cp.code, f);
                    }}
                  />
                </Button>
              </Box>
              {files.length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {files.map((f, idx) => (
                    <Box key={f.id || idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                      {(() => {
                        if (f.isImage && f.previewUrl) {
                          return (
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '4px',
                                overflow: 'hidden',
                                border: '1px solid',
                                borderColor: 'divider',
                                cursor: onPreview ? 'pointer' : 'default',
                              }}
                              onClick={() => {
                                if (onPreview) onPreview({ url: f.previewUrl!, name: f.name, type: 'image' });
                              }}
                            >
                              <img src={f.previewUrl} alt={f.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </Box>
                          );
                        }
                        if (f.isPdf) {
                          return (
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '4px',
                                bgcolor: '#FFEBEE',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '10px',
                                color: '#C62828',
                                cursor: f.previewUrl && onPreview ? 'pointer' : 'default',
                              }}
                              onClick={() => {
                                if (f.previewUrl && onPreview) onPreview({ url: f.previewUrl, name: f.name, type: 'pdf' });
                              }}
                            >
                              PDF
                            </Box>
                          );
                        }
                        return (
                          <Box sx={{ width: 36, height: 36, borderRadius: '4px', bgcolor: '#FFF3E0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>
                            📄
                          </Box>
                        );
                      })()}
                      <IconButton size="small" onClick={() => onDelete(rule.docType, cp.code, f.id)} sx={{ color: '#E53935' }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
